import { useEffect, useState, useCallback } from 'react'
import { api } from './api'

/** Generic data-fetching hook with loading + error state. */
export function useAsync<T>(
  fetcher: () => Promise<T>,
  deps: unknown[],
): { data: T | null; loading: boolean; error: string | null; refetch: () => void } {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    fetcher()
      .then((d) => { if (!cancelled) { setData(d); setLoading(false) } })
      .catch((e: Error) => { if (!cancelled) { setError(e.message); setLoading(false) } })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])

  const refetch = useCallback(() => setTick((t) => t + 1), [])
  return { data, loading, error, refetch }
}

// ── Typed API calls ───────────────────────────────────────────────────────────

export interface UserProfile {
  user: { id: string; email: string; tenant_id: string; created_at: number }
  tenant: { id: string; domain: string; name: string }
  default_namespace: NamespaceDetail
  is_new_user: boolean
}

export interface NamespaceDetail {
  id: string
  slug: string
  name: string
  is_private: number
  allow_overwrite: number
  role: string
  member_count: number
  module_count: number
  created_at: number
}

export interface ModuleListItem {
  id: string
  name: string
  provider: string
  description: string | null
  latest_version: string | null
  download_count: number
  deprecated: boolean
  created_at: number
}

export interface ModuleVersion {
  id: string
  version: string
  status: 'pending' | 'published'
  deprecated_at: number | null
  deprecated_message: string | null
  published_by: string
  published_at: number
}

export interface RegistryVersion {
  id: string
  namespace: string
  name: string
  provider: string
  version: string
  published_at: string
  description: string | null
  deprecated: boolean
  deprecated_message: string | null
}

export interface MachineClient {
  id: string
  namespace_id: string
  auth0_client_id: string
  name: string
  description: string | null
  created_by: string
  created_at: number
  last_rotated_at: number | null
}

export interface Member {
  user_id: string
  email: string
  role: string
  joined_at: number
}

export interface TenantAnalytics {
  total_downloads: number
  downloads_last_30_days: number
  top_modules: Array<{
    namespace: string
    name: string
    provider: string
    latest_version: string | null
    download_count: number
  }>
  daily_series: Array<{ date: string; count: number }>
}

export interface NamespaceAnalytics {
  namespace: string
  total_downloads: number
  downloads_last_30_days: number
  top_modules: Array<{ namespace: string; name: string; provider: string; latest_version: string | null; download_count: number }>
  top_actors: Array<{ actor_id: string; actor_name: string | null; actor_type: string; download_count: number }>
  daily_series: Array<{ date: string; count: number }>
}

export const useProfile     = () => useAsync<UserProfile>(() => api.get('/api/auth/me'), [])
export const useNamespaces  = () => useAsync<{ namespaces: NamespaceDetail[] }>(() => api.get('/api/namespaces'), [])
export const useNamespace   = (slug: string) => useAsync<NamespaceDetail>(() => api.get(`/api/namespaces/${slug}`), [slug])
export const useModules     = (slug: string, search = '') =>
  useAsync<{ modules: ModuleListItem[]; total: number }>(
    () => api.get(`/api/namespaces/${slug}/modules?search=${encodeURIComponent(search)}&limit=50`),
    [slug, search],
  )
export const useVersions    = (slug: string, name: string, provider: string) =>
  useAsync<{ modules: [{ versions: Array<{ version: string }> }] }>(
    () => api.get(`/v1/modules/${slug}/${name}/${provider}/versions`),
    [slug, name, provider],
  )
export const useTokens      = (slug: string) =>
  useAsync<{ tokens: MachineClient[] }>(() => api.get(`/api/namespaces/${slug}/tokens`), [slug])
export const useMembers     = (slug: string) =>
  useAsync<{ members: Member[] }>(() => api.get(`/api/namespaces/${slug}/members`), [slug])
export const useTenantAnalytics    = () => useAsync<TenantAnalytics>(() => api.get('/api/analytics'), [])
export const useNamespaceAnalytics = (slug: string) =>
  useAsync<NamespaceAnalytics>(() => api.get(`/api/namespaces/${slug}/analytics`), [slug])
